/* @path: /src/pages/shelbula/workspaces/CloneRepoModal.jsx */
// @version: 1.1 (The Law of Diagnostic Scrutiny)
// @description: CONSTITUTIONAL AMENDMENT. The `fetchRepos` function now inspects the API response for a `warning` field. If present, it sets this as an error message in the UI, providing the user with specific, actionable feedback when a PAT is missing the required 'repo' scope. This transforms a silent failure ("No repositories found") into a clear diagnostic.
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, Github, Search, GitBranch, Star, Lock, AlertTriangle, Link2 } from 'lucide-react';
import { apiManager } from './apiManager';
import { authManager } from '@/managers/AuthManager';

/* ========================
 * RepoListItem - A button component for the repository list
 * ========================*/
const RepoListItem = ({ repo, onSelect }) => (
    <button
        onClick={() => onSelect(repo)}
        className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors rounded-lg hover:bg-surface-dark"
    >
        <Github size={18} className="text-on-surface-dim flex-shrink-0 group-hover:text-primary transition-colors" />
        <div className="flex-1 overflow-hidden">
            <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-primary">{repo.full_name}</p>
                {repo.private && <Lock size={14} className="text-on-surface-dim flex-shrink-0" title="Private Repository" />}
            </div>
            <div className="mt-1 flex items-center gap-4 text-xs text-on-surface-dim">
                {repo.language && <span className="flex items-center gap-1.5">{repo.language}</span>}
                <span className="flex items-center gap-1.5"><Star size={12} />{repo.stargazers_count}</span>
                <span className="flex items-center gap-1.5"><GitBranch size={12} />{repo.default_branch}</span>
            </div>
        </div>
    </button>
);

/* ========================
 * CloneRepoModal - Modal for cloning a GitHub repository into the current workspace
 * ========================*/
const CloneRepoModal = ({ isOpen, onClose, onConfirm, isCloning, isGithubPatReady, workspaceId }) => {
    // Component Mount State
    const [isMounted, setIsMounted] = useState(false);
    const [authState, setAuthState] = useState(authManager.getState());

    // UI and Data State
    const [error, setError] = useState(null);
    const [repos, setRepos] = useState([]);
    const [isLoadingRepos, setIsLoadingRepos] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRepoListVisible, setIsRepoListVisible] = useState(false);

    // Form Inputs
    const [repoUrl, setRepoUrl] = useState('');

    useEffect(() => {
        setIsMounted(true);
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);
    
    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setError(null);
            setRepoUrl('');
            setSearchTerm('');
            setRepos([]);
            setIsRepoListVisible(false);
            setIsLoadingRepos(false);
        }
    }, [isOpen]);

    // Fetch repositories when user wants to see them
    useEffect(() => {
        if (isOpen && isRepoListVisible && isGithubPatReady && authState.user && repos.length === 0) {
            const fetchRepos = async () => {
                setIsLoadingRepos(true);
                setError(null);
                try {
                    const data = await apiManager.listMyGithubRepos();
                    setRepos(data.repos || []);
                    if (data.warning) {
                        setError(data.warning); // Display the helpful warning from the server
                    }
                } catch (err) {
                    setError(`Failed to load repositories: ${err.message}`);
                    setRepos([]);
                } finally {
                    setIsLoadingRepos(false);
                }
            };
            fetchRepos();
        }
    }, [isOpen, isRepoListVisible, isGithubPatReady, authState.user, repos.length]);


    const handleSelectRepo = (repo) => {
        setRepoUrl(repo.clone_url);
        setIsRepoListVisible(false);
    };

    const filteredRepos = useMemo(() => {
        if (!searchTerm) return repos;
        return repos.filter(repo => repo.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [repos, searchTerm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!repoUrl.trim() || isCloning) return;
        
        setError(null);
        // The parent component is responsible for the actual clone logic
        // and returns an error message if it fails.
        const errorMessage = await onConfirm({ repoUrl, workspaceId });
        if (errorMessage) {
            setError(errorMessage);
        }
    };
    
    if (!isMounted) return null;

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="relative w-full max-w-2xl bg-surface rounded-lg border border-border-color shadow-xl flex flex-col max-h-[80vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-4 border-b border-border-color flex-shrink-0">
                            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2.5"><Github size={20} className="text-primary"/> Clone a Repository</h2>
                            <button onClick={onClose} className="p-1 rounded-full text-on-surface-dim hover:bg-surface-dark hover:text-on-surface">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="repoUrl" className="text-sm font-medium text-on-surface flex items-center gap-2 mb-2"><Link2 size={14}/>Repository URL</label>
                                    <input
                                        id="repoUrl" type="text" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)}
                                        placeholder="https://github.com/owner/repo.git" required
                                        className="w-full px-3 py-1.5 bg-background-dark border border-border-color rounded-md text-on-surface focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-colors duration-200"
                                        autoFocus
                                    />
                                </div>
                                
                                <div className="border-t border-border-color pt-4">
                                     <button
                                        type="button"
                                        onClick={() => setIsRepoListVisible(prev => !prev)}
                                        disabled={!isGithubPatReady}
                                        title={!isGithubPatReady ? "Add a GitHub PAT to list your private repositories" : "Select From Your Repositories"}
                                        className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <GitBranch size={16} />
                                        <span>{isRepoListVisible ? 'Hide Your Repositories' : 'Select From Your Repositories'}</span>
                                    </button>

                                    {!isGithubPatReady && (
                                        <div className="flex items-start gap-3 text-xs text-yellow-400 bg-yellow-500/10 p-3 rounded-md mt-3">
                                            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                            <span>A GitHub Personal Access Token is required to list and clone private repositories. Public repositories can be cloned directly using their URL.</span>
                                        </div>
                                    )}

                                    <AnimatePresence>
                                        {isRepoListVisible && isGithubPatReady && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="border border-border-color rounded-lg overflow-hidden bg-background-dark">
                                                    <div className="relative flex items-center border-b border-border-color">
                                                        <div className="absolute left-0 flex items-center pl-3 pointer-events-none">
                                                            <Search size={16} className="text-on-surface-dim" />
                                                        </div>
                                                        <input
                                                            type="text"
                                                            placeholder="Search repositories..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            className="w-full pl-9 pr-3 py-1.5 bg-transparent border-0 text-on-surface focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                        {isLoadingRepos ? (
                                                            <div className="flex items-center justify-center p-8 gap-2 text-on-surface-dim">
                                                                <Loader size={16} className="animate-spin" /> Loading...
                                                            </div>
                                                        ) : filteredRepos.length > 0 ? (
                                                            filteredRepos.map(repo => <RepoListItem key={repo.id} repo={repo} onSelect={handleSelectRepo} />)
                                                        ) : (
                                                            <p className="text-center text-on-surface-dim text-sm p-4">No repositories found.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </form>
                            {error && (
                                <div className="flex items-start gap-3 text-sm text-error-bubble-text bg-error-bubble border border-error-bubble-border p-3 rounded-lg mt-4">
                                    <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="p-4 border-t border-border-color flex-shrink-0">
                             <div className="flex justify-end items-center gap-3">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md text-on-surface-dim hover:bg-surface-dark transition-colors">Cancel</button>
                                <button 
                                    type="submit"
                                    onClick={handleSubmit} 
                                    disabled={isCloning || !repoUrl.trim()}
                                    className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark transition-colors disabled:bg-primary/50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
                                >
                                    {isCloning ? <Loader size={16} className="animate-spin" /> : 'Clone'}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
    
    return ReactDOM.createPortal(modalContent, document.body);
};

export default CloneRepoModal;